import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  addArticleComment,
  getArticleComments,
  deleteArticleComment,
  updateArticleComment,
  toggleArticleCommentLike,
  toggleArticleLike
} from '../../services/firestoreService';
import { handleError, handleSuccess, handleWarning } from '../../services/errorHandlingService';

const ArticleComments = ({ article, isLoggedIn, isAdmin, isMember }) => {
  const { currentUser, userProfile } = useAuth();
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [articleLiked, setArticleLiked] = useState(false);
  const [articleLikeCount, setArticleLikeCount] = useState(article?.likeCount || 0);
  const [showReactionDropdown, setShowReactionDropdown] = useState(false);
  const [reactionTimeout, setReactionTimeout] = useState(null);

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      const commentsData = await getArticleComments(article.id);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  }, [article.id]);

  useEffect(() => {
    if (article?.id) {
      loadComments();
      // Check if current user has liked this article
      if (currentUser && article.likes) {
        setArticleLiked(article.likes.includes(currentUser.uid));
      }
    }
  }, [article?.id, article?.likes, currentUser, loadComments]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!commentText.trim()) {
      handleWarning('Please enter a comment.');
      return;
    }

    if (!currentUser) {
      handleWarning('You must be logged in to comment.');
      return;
    }

    if (!isMember) {
      handleWarning('You must be a member to comment.');
      return;
    }

    try {
      setSubmitting(true);
      const commentData = {
        content: commentText.trim(),
        authorId: currentUser.uid,
        authorName: userProfile?.firstName && userProfile?.lastName
          ? `${userProfile.firstName} ${userProfile.lastName}`
          : userProfile?.displayName || 'Anonymous User',
        authorEmail: currentUser.email
      };

      await addArticleComment(article.id, commentData);
      setCommentText('');
      await loadComments(); // Reload comments
      handleSuccess('Comment added successfully!');
    } catch (error) {
      handleError(error, {
        context: {
          component: 'ArticleComments',
          action: 'submitComment',
          articleId: article.id
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleArticleLike = async () => {
    if (!currentUser || !isMember) return;

    try {
      const liked = await toggleArticleLike(article.id, currentUser.uid);
      setArticleLiked(liked);
      setArticleLikeCount(prev => liked ? prev + 1 : prev - 1);
    } catch (error) {
      console.error('Error toggling article like:', error);
    }
  };

  const handleCommentLike = async (commentId) => {
    if (!currentUser || !isMember) return;

    try {
      const liked = await toggleArticleCommentLike(commentId, currentUser.uid);
      // Update the comment in the local state
      setComments(prev => prev.map(comment => 
        comment.id === commentId 
          ? {
              ...comment,
              likes: liked 
                ? [...(comment.likes || []), currentUser.uid]
                : (comment.likes || []).filter(uid => uid !== currentUser.uid),
              likeCount: liked ? (comment.likeCount || 0) + 1 : (comment.likeCount || 0) - 1
            }
          : comment
      ));
    } catch (error) {
      console.error('Error toggling comment like:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!isAdmin) {
      handleWarning('You do not have permission to delete comments.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      try {
        await deleteArticleComment(commentId, article.id);
        await loadComments(); // Reload comments
        handleSuccess('Comment deleted successfully.');
      } catch (error) {
        handleError(error, {
          context: {
            component: 'ArticleComments',
            action: 'deleteComment',
            commentId,
            articleId: article.id
          }
        });
      }
    }
  };

  const handleReactionHover = () => {
    if (reactionTimeout) {
      clearTimeout(reactionTimeout);
    }
    const timeout = setTimeout(() => {
      setShowReactionDropdown(true);
    }, 500); // 500ms delay
    setReactionTimeout(timeout);
  };

  const handleReactionLeave = () => {
    if (reactionTimeout) {
      clearTimeout(reactionTimeout);
    }
    setTimeout(() => {
      setShowReactionDropdown(false);
    }, 200); // Small delay to allow moving to dropdown
  };

  const reactions = [
    { emoji: '👍', name: 'Like' },
    { emoji: '❤️', name: 'Love' },
    { emoji: '😂', name: 'Laugh' },
    { emoji: '😮', name: 'Wow' },
    { emoji: '😢', name: 'Sad' },
    { emoji: '😡', name: 'Angry' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-md mt-8">
      {/* Article Reactions */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-gray-600">
                {articleLikeCount} {articleLikeCount === 1 ? 'like' : 'likes'}
              </span>
              <span className="text-gray-600">
                {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
              </span>
            </div>
          </div>
          
          {/* Reaction Buttons */}
          <div className="flex items-center space-x-2">
            {isMember ? (
              <>
                <button
                  onClick={handleArticleLike}
                  className={`flex items-center space-x-1 px-3 py-1 rounded-md transition-colors ${
                    articleLiked 
                      ? 'bg-apn-blue text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>👍</span>
                  <span>{articleLiked ? 'Liked' : 'Like'}</span>
                </button>
                
                <div 
                  className="relative"
                  onMouseEnter={handleReactionHover}
                  onMouseLeave={handleReactionLeave}
                >
                  <button className="flex items-center space-x-1 px-3 py-1 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
                    <span>😊</span>
                    <span>React</span>
                  </button>
                  
                  {showReactionDropdown && (
                    <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex space-x-1 z-10">
                      {reactions.map((reaction) => (
                        <button
                          key={reaction.name}
                          onClick={() => {
                            // For now, just use the like function for all reactions
                            handleArticleLike();
                            setShowReactionDropdown(false);
                          }}
                          className="p-2 hover:bg-gray-100 rounded transition-colors"
                          title={reaction.name}
                        >
                          <span className="text-xl">{reaction.emoji}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-gray-500 text-sm">
                {isLoggedIn ? 'Membership required to react' : 'Login to react'}
              </div>
            )}
            
            {/* Share Buttons */}
            <button
              onClick={() => {
                const url = window.location.href;
                const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
                window.open(linkedinUrl, '_blank');
              }}
              className="flex items-center space-x-1 px-3 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              <span>📤</span>
              <span>LinkedIn</span>
            </button>
            
            <button
              onClick={() => {
                const url = window.location.href;
                const shareText = `Check out this article: ${article.title}`;
                const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`;
                window.open(twitterUrl, '_blank');
              }}
              className="flex items-center space-x-1 px-3 py-1 rounded-md bg-black text-white hover:bg-gray-800 transition-colors"
            >
              <span>📤</span>
              <span>X</span>
            </button>
          </div>
        </div>
      </div>

      {/* Comment Form */}
      {isMember ? (
        <div className="p-4 border-b border-gray-200">
          <form onSubmit={handleSubmitComment} className="space-y-4">
            <div>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Share your thoughts on this article..."
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue resize-none"
                rows="3"
                disabled={submitting}
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!commentText.trim() || submitting}
                className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="p-4 border-b border-gray-200 text-center">
          <p className="text-gray-600">
            {isLoggedIn 
              ? 'Membership required to comment on articles.' 
              : 'Please log in and become a member to comment on articles.'
            }
          </p>
        </div>
      )}

      {/* Comments List */}
      <div className="p-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-apn-blue mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading comments...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Be the first to comment on this article!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {comments.map((comment) => (
              <div key={comment.id} className="flex">
                <div className="flex-shrink-0 mr-3">
                  <div className="h-10 w-10 rounded-full bg-apn-blue text-white flex items-center justify-center">
                    {comment.authorName ? comment.authorName.charAt(0).toUpperCase() : 'U'}
                  </div>
                </div>
                <div className="flex-grow">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{comment.authorName || 'Anonymous'}</h4>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          {comment.createdAt?.toDate?.() ? 
                            comment.createdAt.toDate().toLocaleDateString() : 
                            'Recently'
                          }
                        </span>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                            title="Delete comment"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-700 mb-3">{comment.content}</p>
                    <div className="flex items-center space-x-4">
                      {isMember ? (
                        <button
                          onClick={() => handleCommentLike(comment.id)}
                          className={`flex items-center space-x-1 text-sm ${
                            comment.likes?.includes(currentUser?.uid)
                              ? 'text-apn-blue'
                              : 'text-gray-500 hover:text-apn-blue'
                          }`}
                        >
                          <span>👍</span>
                          <span>{comment.likeCount || 0}</span>
                        </button>
                      ) : (
                        <span className="flex items-center space-x-1 text-sm text-gray-500">
                          <span>👍</span>
                          <span>{comment.likeCount || 0}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleComments;
